import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { adjustMemberTimeOffBalance } from '../api/member-time-offs.api';
import { getMemberTimeOffBalancesQueryKey } from '../constants/member-time-offs-query-keys';
import type { TimeOffBalance } from '../types/time-off-balance';

interface AdjustMemberTimeOffBalanceVars {
  typeId: number;
  newAvailable: number;
  previousAvailable: number;
}

const calculateBalanceAdjustmentAmount = (
  newAvailable: number,
  previousAvailable: number
): number => Number((newAvailable - previousAvailable).toFixed(2));

/**
 * Sets the absolute "Available" days for a member's balance. The backend
 * endpoint accepts an adjustment delta, so the hook converts from the modal's
 * absolute value into `amount`.
 */
export const useAdjustMemberTimeOffBalance = (
  memberId: string,
  { onSuccess }: { onSuccess?: () => void } = {}
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.timeOff');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: ({ typeId, newAvailable, previousAvailable }: AdjustMemberTimeOffBalanceVars) =>
      adjustMemberTimeOffBalance({
        userId: Number(memberId),
        timeOffTypeId: typeId,
        amount: calculateBalanceAdjustmentAmount(newAvailable, previousAvailable),
      }),
    onSuccess: async (_, { typeId, newAvailable }) => {
      // The adjust endpoint accepts a delta and returns no updated balance, but
      // the modal already knows the new visible value. Patch that field
      // immediately, then refetch to reconcile backend-calculated totals.
      queryClient.setQueryData<TimeOffBalance[]>(
        getMemberTimeOffBalancesQueryKey(memberId),
        balances =>
          balances?.map(balance =>
            balance.type.id === typeId ? { ...balance, availableDays: newAvailable } : balance
          )
      );
      await queryClient.invalidateQueries({
        queryKey: getMemberTimeOffBalancesQueryKey(memberId),
      });
      displayToastSuccess(t('member.adjust.toastSuccess'));
      onSuccess?.();
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'member.adjust.toastError');
    },
  });
};
